import React, { useEffect, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { CountDetail, CountPending, ScanLight, ScanLookup, ScanProduct, ScanSuggestion } from "../../../../services/v2/stockops.service";
import ProductPicker from "../products/productPicker";
import { apiError, ui, useDebounced } from "../kits/shared";
import { parseDecimal } from "../../../../components/kits/kitUi";
import { ProductRow } from "../../../../services/v2/inventory.service";
import { EMPTY_DRAFT, VariantAxes, VariantDraft, sortSizes } from "../products/variantes";
import AvisoTalla from "../products/avisoTalla";
import { TallaEnNombre } from "../../../../utils/tallaEnNombre";
import MatrizCantidades, { cellKey, cellLabel, typedCells } from "./matrizCantidades";
import ContarPorTallas from "./contarPorTallas";
import { beep, unlockAudio, useBarcodeCamera } from "../../../../components/escaner/camara";

/*
	Escáner de conteos por celular (Esteban, 2026-10-15). Las asesoras cuentan con Chrome en Android: la cámara lee el código
	de barras y suma una pieza al conteo en el servidor (nada se pierde si se cae la señal o se bloquea el celular).
	Lo que no existe en WAPP: foto → la IA propone nombre, marca, detalle y talla → se da de alta (quien tiene permiso de
	Productos) o queda "por dar de alta" para la jefa. Chrome en Android trae lector propio (BarcodeDetector); donde no hay,
	se carga ZXing solo en ese momento.
*/

const UNITS = ["pieza", "par", "juego", "caja", "paquete", "frasco", "botella", "rollo", "metro", "kilo", "litro"];
/** El mismo código a la vista no vuelve a contar hasta que sale de cuadro este tiempo. */
const SAME_CODE_GAP_MS = 1500;
/** After an alta (or cancelling it) the product is usually still in front of the camera: its code does not count for this long. */
const HOLD_AFTER_ALTA_MS = 4000;
/** A new code that shows up while another one was in view this recently is a second code on the same box: it is skipped. */
const TWO_CODES_MS = 400;

type SizeTypes = Record<string, { label: string; values: { value: string; label: string }[]; allowCustom?: boolean }>;

/** Los renglones "por dar de alta", juntando en uno los que se capturaron juntos (varias tallas/colores del mismo producto). */
export const pendingGroups = (rows: CountPending[]): { first: CountPending; rows: CountPending[]; pieces: number; label: string }[] => {
	const groups: { first: CountPending; rows: CountPending[]; pieces: number; label: string }[] = [];
	const byBatch: Record<string, number> = {};
	rows.forEach((row) => {
		if (row.batchKey && byBatch[row.batchKey] !== undefined) {
			const group = groups[byBatch[row.batchKey]];
			group.rows.push(row);
			group.pieces += row.quantity;
			return;
		}
		if (row.batchKey) byBatch[row.batchKey] = groups.length;
		groups.push({ first: row, rows: [row], pieces: row.quantity, label: "" });
	});
	groups.forEach((group) => {
		const matrix = group.first.matrix;
		group.label = group.first.batchKey && matrix
			? [matrix.sizes.length ? (matrix.sizes.length === 1 ? matrix.sizes[0] : matrix.sizes.length + " tallas") : "", matrix.colors.length ? (matrix.colors.length === 1 ? matrix.colors[0] : matrix.colors.length + " colores") : ""].filter(Boolean).join(" × ")
			: [group.first.sizeValue, group.first.colorValue].filter(Boolean).join(" ");
	});
	return groups;
};

/** Símbolo de la moneda del país del plantel: lempiras en Honduras (y Online), pesos en México. */
export const currencyOf = (countryCode: string | null | undefined): string => countryCode === "HN" ? "L" : countryCode === "MX" ? "$" : "";

// Campos blancos con borde visible: sobre el papel de WAPP, un campo transparente no se distingue. 16 px: iOS hace zoom a
// toda la página al tocar un campo con letra más chica.
const touch: React.CSSProperties = { height: "42px", fontSize: "16px", backgroundColor: "var(--wa-superficie)", border: "1.5px solid var(--wa-filo-2)", borderRadius: "10px" };
// A disabled field must look disabled (the inline background above hid the gray of :disabled).
const touchField = (off: boolean): React.CSSProperties => off ? { ...touch, backgroundColor: "var(--wa-papel-2)", color: "var(--wa-apagado)" } : touch;
// Buttons keep their Bootstrap colors: the field background above made btn-primary white-on-white.
const touchBtn: React.CSSProperties = { height: "42px", fontSize: "15px" };
const sheet: React.CSSProperties = { position: "fixed", inset: 0, zIndex: 2100, background: "var(--wa-papel)", overflowY: "auto", display: "flex", flexDirection: "column" };
const bar: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", padding: "10px 14px", background: "var(--wa-superficie)", borderBottom: "1px solid var(--wa-filo)", position: "sticky", top: 0, zIndex: 1 };

/** Error of a scanner request: with no answer from the server we cannot know whether it was saved. */
const scanError = (requestError: any, fallback: string): string => requestError?.response
	? apiError(requestError, fallback)
	: "Sin señal: no se supo si se guardó. Revisa «Lo Último que Escaneaste» antes de volver a escanear.";

/** Achica la foto antes de subirla (las del celular pesan 3–6 MB): 1280 px de lado mayor, JPEG. */
export const shrinkImage = async (file: File, max = 1280): Promise<File> => {
	try {
		const bitmap = await createImageBitmap(file);
		const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
		const canvas = document.createElement("canvas");
		canvas.width = Math.round(bitmap.width * scale);
		canvas.height = Math.round(bitmap.height * scale);
		const context = canvas.getContext("2d");
		if (!context) return file;
		context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
		const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
		return blob ? new File([blob], (file.name || "foto").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }) : file;
	} catch {
		return file;
	}
};


interface Notice { tone: "ok" | "warn"; title: string; detail?: string; articleId?: number; pendingId?: number; code?: string | null; serialized?: boolean }
type Option = { kind: "product"; product: ScanProduct } | { kind: "pending"; pending: CountPending };
type Creating = { code: string | null; lookup: ScanLookup | null; shared: boolean; key: number };

/** Foto chiquita de un producto o de un "por dar de alta", para confirmar a simple vista que es lo que se escaneó. */
const Thumb: React.FC<{ campusId: number; countId: number; articleId?: number; pendingId?: number; size?: number }> = ({ campusId, countId, articleId, pendingId, size = 64 }) => {
	const [url, setUrl] = useState<string | null>(null);
	useEffect(() => {
		let alive = true;
		let made: string | null = null;
		setUrl(null);
		const http = HttpManager.getInstance().stockOpsService;
		const call = articleId ? http.productPhoto(campusId, articleId, true) : pendingId ? http.scanPendingPhoto(campusId, countId, pendingId, true) : null;
		if (call) call.then((response) => { if (!alive) return; made = URL.createObjectURL(response.data); setUrl(made); }).catch(() => undefined);
		return () => { alive = false; if (made) URL.revokeObjectURL(made); };
	}, [campusId, countId, articleId, pendingId]);
	return <div style={{ width: size + "px", height: size + "px", flex: "0 0 auto", borderRadius: "8px", overflow: "hidden", border: "1px solid var(--wa-filo)", background: "var(--wa-papel-2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
		{url ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={ui.tiny}>Sin foto</span>}
	</div>;
};

/** A typed text is a code (barcode or WA code) when it has no spaces and at least 4 digits; anything else is a name to search. */
const looksLikeCode = (text: string): boolean => !/\s/.test(text) && /\d{4,}/.test(text);

const Escaner: React.FC<{ campusId: number; count: CountDetail; startNoCode?: boolean; onCount: (count: CountDetail) => void; onClose: () => void }> = ({ campusId, count, startNoCode, onCount, onClose }) => {
	const http = HttpManager.getInstance().stockOpsService;
	const videoRef = useRef<HTMLVideoElement>(null);
	const busyRef = useRef<boolean>(false);
	// code → last time the camera saw it. Every code in view is renewed on every frame, so each one has its own gap.
	const seen = useRef<Map<string, number>>(new Map());
	const hold = useRef<{ code: string; until: number }>({ code: "", until: 0 });
	const creatingRef = useRef<boolean>(!!startNoCode);
	// Opened from the count screen's "Producto Nuevo Sin Código": closing that first form goes back to the count (no camera).
	const fromCountScreen = useRef<boolean>(!!startNoCode);
	const [busy, setBusy] = useState<boolean>(false);
	const [notice, setNotice] = useState<Notice | null>(null);
	const [choice, setChoice] = useState<{ title: string; code: string | null; options: Option[]; allowOther: boolean } | null>(null);
	// "Producto Nuevo Sin Código" from the count screen opens the scanner straight in the new-product form (camera stays off).
	const [creating, setCreatingState] = useState<Creating | null>(startNoCode ? { code: null, lookup: null, shared: false, key: Date.now() } : null);
	const [typed, setTyped] = useState<string>("");
	const [adding, setAdding] = useState<string | null>(null);
	const [sizeTypes, setSizeTypes] = useState<SizeTypes>({});
	const [colorSuggestions, setColorSuggestions] = useState<string[]>([]);
	const [currency, setCurrency] = useState<string>("");
	const [totals, setTotals] = useState<{ products: number; pieces: number }>(() => ({ products: count.lines.length, pieces: count.lines.reduce((sum, line) => sum + line.counted, 0) }));
	const [recent, setRecent] = useState<number[]>([]);
	const [flash, setFlash] = useState<boolean>(false);
	// Buscar por nombre: lo que no tiene código (o se le cayó la etiqueta) se cuenta eligiéndolo, sin darlo de alta otra vez.
	const [searching, setSearching] = useState<string | null>(null);
	// Producto que ya existe con tallas/colores: se cuentan todas de una vez.
	const [countingModel, setCountingModel] = useState<number | null>(null);
	const searchText = useDebounced(searching ?? "", 300);
	const [found, setFound] = useState<ProductRow[] | null>(null);
	const [searchFailed, setSearchFailed] = useState<boolean>(false);
	const camera = useBarcodeCamera(videoRef, creating === null && searching === null && countingModel === null, (codes) => onCodes(codes));

	// The ref changes at once (not at the next render): a code read in between cannot open a second alta over the first one.
	const setCreating = (value: Creating | null) => { creatingRef.current = value !== null; setCreatingState(value); };

	useEffect(() => { unlockAudio(); }, []);
	useEffect(() => {
		if (!flash) return;
		const timer = setTimeout(() => setFlash(false), 400);
		return () => clearTimeout(timer);
	}, [flash]);

	useEffect(() => {
		const text = searchText.trim();
		if (searching === null || text.length < 2) { setFound(null); setSearchFailed(false); return; }
		let alive = true;
		setFound(null);
		setSearchFailed(false);
		HttpManager.getInstance().inventoryService.list(campusId, { q: text, kinds: ["article", "vitrina"], includeVariants: 1 }, 1, 30)
			.then((response) => {
				if (!alive) return;
				// A product with sizes/colors shows once (its sizes are counted all together); a size found by itself brings its product.
				const out: ProductRow[] = [];
				((response.data.data || []) as ProductRow[]).forEach((row) => {
					const entry: ProductRow | null = row.variantCount > 0 ? row
						: row.modelId ? { ...row, id: row.modelId, modelId: null, name: row.name.split(" · ")[0], variantCount: 1, sizeValue: null, colorValue: null, barcode: null }
						: row.trackStock ? row : null;
					if (entry && !out.some((other) => other.id === entry.id)) out.push(entry);
				});
				setFound(out.slice(0, 12));
			})
			// A failed search is not "it does not exist": never offer "Producto Nuevo" on a network error (that made duplicates).
			.catch(() => { if (alive) { setFound([]); setSearchFailed(true); } });
		return () => { alive = false; };
	}, [searchText, campusId, searching === null]);

	const pickFound = async (row: ProductRow) => {
		setSearching(null);
		setFound(null);
		if (row.variantCount > 0) { setCountingModel(row.id); return; }
		await addProduct({ id: row.id, name: row.name + (row.sizeValue && !row.name.includes(row.sizeValue) ? " · " + row.sizeValue : ""), waCode: row.waCode, barcode: row.barcode, sizeValue: row.sizeValue, serialized: !!row.serialized, hasPhoto: row.hasPhoto }, 1, null);
	};

	/**
	 * Cada lectura trae solo lo que cambió (no el conteo entero, que en una bodega grande son 1,500 renglones): se mete ese renglón
	 * en la lista, se actualizan totales, pendientes y quién escaneó. Al cerrar el escáner el conteo se recarga completo una vez.
	 */
	const merge = (data: ScanLight | CountDetail) => {
		if (!(data as ScanLight).light) {
			const full = data as CountDetail;
			setTotals({ products: full.lines.length, pieces: full.lines.reduce((sum, line) => sum + (line.second ?? line.counted), 0) });
			onCount(full);
			return;
		}
		const light = data as ScanLight;
		const line = light.line;
		const lines = line === null ? count.lines
			: count.lines.some((entry) => entry.articleId === line.articleId) ? count.lines.map((entry) => entry.articleId === line.articleId ? line : entry) : [...count.lines, line];
		onCount({ ...count, lines, pending: light.pending, scanners: light.scanners });
		setTotals({ products: light.lineCount, pieces: light.pieces });
		if (line !== null) setRecent((ids) => [line.articleId, ...ids.filter((id) => id !== line.articleId)].slice(0, 30));
	};

	const loadCatalogs = () => {
		HttpManager.getInstance().inventoryService.catalogs(campusId).then((response) => {
			setSizeTypes(response.data.sizeTypes || {});
			setColorSuggestions(response.data.colorSuggestions || []);
			setCurrency(currencyOf(response.data.countryCode));
		}).catch(() => setSizeTypes({}));
	};
	useEffect(() => { loadCatalogs(); }, [campusId]);

	/** Abre el alta; si las tallas no cargaron al abrir el escáner (señal), las vuelve a pedir. */
	const openCreating = (code: string | null, lookup: ScanLookup | null, shared: boolean) => {
		if (Object.keys(sizeTypes).length < 1) loadCatalogs();
		setCreating({ code, lookup, shared, key: Date.now() });
	};

	const run = async (call: () => Promise<any>, fallback: string): Promise<any> => {
		busyRef.current = true;
		setBusy(true);
		try {
			return await call();
		} catch (requestError: any) {
			setNotice({ tone: "warn", title: scanError(requestError, fallback) });
			return null;
		} finally {
			busyRef.current = false;
			setBusy(false);
		}
	};

	const pieces = (qty: number) => qty + (qty === 1 ? " pieza" : " piezas");
	const confirmPiece = () => {
		if (navigator.vibrate) navigator.vibrate(60);
		beep();
		setFlash(true);
	};

	/** Suma (o resta, con delta negativo) piezas de un producto. En equipo nadie fija totales: cada quien suma lo suyo. */
	const addProduct = async (product: ScanProduct, delta: number, code: string | null) => {
		const response = await run(() => http.scanAdd(campusId, count.id, product.id, delta, null, true), "No se pudo sumar.");
		if (!response) return;
		merge(response.data);
		setChoice(null);
		setAdding(null);
		const qty = response.data.line?.counted ?? 0;
		// Matched by its WA code: that code belongs to this product only, so "No Es Este" would lead nowhere.
		const byWaCode = !!code && !!product.waCode && product.waCode.toLowerCase() === code.toLowerCase();
		setNotice({ tone: "ok", title: product.name, detail: pieces(qty), articleId: product.id, code: byWaCode ? null : code, serialized: !!product.serialized || !!response.data.line?.serialized });
		if (delta > 0) confirmPiece();
	};

	const addToPending = async (pending: CountPending, delta: number, code: string | null) => {
		const response = await run(() => http.scanPending(campusId, count.id, { pendingId: pending.id, quantity: delta }, null, true), "No se pudo sumar.");
		if (!response) return;
		merge(response.data);
		setChoice(null);
		setAdding(null);
		const row = response.data.pending.find((entry: CountPending) => entry.id === pending.id);
		setNotice({ tone: "ok", title: pending.name + (pending.sizeValue ? " · " + pending.sizeValue : ""), detail: "Por dar de alta · " + pieces(row ? row.quantity : delta), pendingId: pending.id, code });
		confirmPiece();
	};

	const pick = (option: Option, code: string | null) => option.kind === "product" ? addProduct(option.product, 1, code) : addToPending(option.pending, 1, code);

	/** Pregunta cuál es (tallas o código compartido); el aviso del escaneo anterior se quita para no confundir. */
	const ask = (next: { title: string; code: string | null; options: Option[]; allowOther: boolean }) => { setNotice(null); setChoice(next); };

	const handle = async (code: string) => {
		if (creatingRef.current || busyRef.current) return;
		setChoice(null);
		setAdding(null);
		const response = await run(() => http.scanLookup(campusId, code, count.id), "No se pudo buscar el código.");
		if (!response) return;
		const found: ScanLookup = response.data;
		const pendings = found.pendings || (found.pending ? [found.pending] : []);
		if (found.status === "wapp" && found.products) {
			const options: Option[] = [...found.products.map((product): Option => ({ kind: "product", product })), ...pendings.map((pending): Option => ({ kind: "pending", pending }))];
			// Un solo producto con ese código: suma directo. Código compartido: se pregunta, con fotos.
			if (options.length === 1) await pick(options[0], code);
			else ask({ title: "Este código lo tienen varios. ¿Cuál es?", code, options, allowOther: true });
		} else if (found.status === "sizes" && found.products) {
			// The product's own code (not a size's): all its sizes/colors are counted at once.
			if (found.model) setCountingModel(found.model.id);
			else setNotice({ tone: "warn", title: "Ese producto no tiene tallas dadas de alta." });
		} else if (found.status === "notCountable") {
			setNotice({ tone: "warn", title: found.message || "Ese producto no se cuenta así." });
		} else if (found.status === "pending" && pendings.length > 0) {
			if (pendings.length === 1) await addToPending(pendings[0], 1, code);
			else ask({ title: "Este código lo tienen varios. ¿Cuál es?", code, options: pendings.map((pending): Option => ({ kind: "pending", pending })), allowOther: true });
		} else {
			openCreating(code, found, false);
		}
	};

	/** "No es este": quita la pieza que se acaba de sumar y da de alta el otro producto con el mismo código. */
	const notThisOne = async () => {
		if (!notice) return;
		const code = notice.code || null;
		if (notice.articleId) {
			const response = await run(() => http.scanAdd(campusId, count.id, notice.articleId!, -1, null, true), "No se pudo quitar la pieza.");
			if (!response) return;
			merge(response.data);
		} else if (notice.pendingId) {
			const row = count.pending.find((entry) => entry.id === notice.pendingId);
			// A cell of a grid only gives back its piece (it can go to 0); discarding it would discard the whole product.
			const response = await run(() => row && (row.quantity > 1 || row.batchKey) ? http.scanPending(campusId, count.id, { pendingId: row.id, quantity: -1 }, null, true) : http.scanPendingDiscard(campusId, count.id, notice.pendingId!, true), "No se pudo quitar la pieza.");
			if (!response) return;
			merge(response.data);
		}
		setNotice(null);
		openCreating(code, null, code !== null);
	};

	const onCodes = (codes: string[]) => {
		const now = Date.now();
		const inFrame = Array.from(new Set(codes.filter(Boolean)));
		if (inFrame.length < 1) return;
		if (inFrame.length > 1) {
			// Two codes in the same frame (EAN + QR on one box): none counts until one is covered. A code that already counted stays
			// "seen" (it must not count again when the other is covered); a new one is not marked, so it counts once left alone.
			inFrame.forEach((code) => { const at = seen.current.get(code); if (at !== undefined && now - at < SAME_CODE_GAP_MS) seen.current.set(code, now); });
			const unread = inFrame.some((code) => { const at = seen.current.get(code); return at === undefined || now - at >= SAME_CODE_GAP_MS; });
			if (unread && !busyRef.current && choice === null && !creatingRef.current) setNotice({ tone: "warn", title: "Hay dos códigos a la vista: tapa uno con el dedo." });
			return;
		}
		const code = inFrame[0];
		const previous = seen.current.get(code);
		const otherJustSeen = Array.from(seen.current.entries()).some(([other, at]) => other !== code && now - at < TWO_CODES_MS);
		const held = hold.current.code === code && now < hold.current.until;
		// Cada vez que la cámara ve el código se renueva la marca, aunque esté ocupada: el mismo producto vuelve a contar solo
		// cuando sale de cuadro y regresa, no cada vez que termina una consulta lenta.
		if (!otherJustSeen) seen.current.set(code, now);
		const fresh = previous === undefined || now - previous >= SAME_CODE_GAP_MS;
		if (!fresh || held || otherJustSeen || busyRef.current || choice !== null || creatingRef.current) return;
		handle(code).finally(() => { seen.current.set(code, Date.now()); });
	};

	/** After an alta or cancelling it the product is still in front of the camera: its code waits until it leaves the frame. */
	const holdCode = (code: string | null) => {
		if (!code) return;
		hold.current = { code, until: Date.now() + HOLD_AFTER_ALTA_MS };
		seen.current.set(code, Date.now());
	};

	const recentLines = recent.map((id) => count.lines.find((line) => line.articleId === id)).filter((line): line is CountDetail["lines"][number] => !!line);
	const noticeLine = notice?.articleId ? count.lines.find((line) => line.articleId === notice.articleId) : null;
	const noticeItem = notice && (notice.articleId || notice.pendingId);
	const addingQty = Math.floor(Number(adding) || 0);

	return <div style={sheet} onPointerDown={unlockAudio}>
		<div style={bar}>
			<div style={{ minWidth: 0 }}>
				<div style={{ fontWeight: 700, fontSize: "16px" }}>Escanear · <span style={{ fontFamily: "monospace" }}>{count.folio}</span></div>
				<div style={ui.small}>{count.warehouse} · {totals.products} productos · {totals.pieces} piezas</div>
			</div>
			{/* Never disabled: a request hung by a weak signal must not trap the person inside the scanner. */}
			<button type="button" className="btn btn-primary rounded-pill" style={{ flex: "0 0 auto" }} onClick={onClose}>Listo</button>
		</div>

		<div style={{ position: "relative", background: "#000", height: "38vh", minHeight: "220px", overflow: "hidden", width: "100%", maxWidth: "720px", margin: "0 auto" }}>
			<video ref={videoRef} muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
			<div style={{ position: "absolute", left: "12%", right: "12%", top: "32%", bottom: "32%", border: flash ? "4px solid var(--wa-verde)" : "3px solid rgba(255,255,255,0.85)", borderRadius: "12px", pointerEvents: "none" }} />
			<div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "8px 12px", color: "#fff", fontSize: "13px", background: "linear-gradient(transparent, rgba(0,0,0,0.6))" }}>
				{searching !== null || creating !== null ? "Cámara en pausa"
					: camera === "starting" ? "Prendiendo la cámara…" : camera === "denied" ? "Permite la cámara en el navegador (candado de la barra de direcciones) y vuelve a abrir."
					: camera === "unsupported" ? "Este navegador no puede usar la cámara. Escribe el código abajo." : busy ? "Buscando…" : choice ? "Elige abajo cuál es" : "Apunta al código de barras"}
			</div>
		</div>

		<div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: "12px", width: "100%", maxWidth: "720px", margin: "0 auto" }}>
			{notice ? <div style={{ ...ui.card, padding: "12px", borderColor: notice.tone === "ok" ? "var(--wa-acento)" : "var(--wa-oro)" }}>
				<div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
					{noticeItem ? <Thumb campusId={campusId} countId={count.id} articleId={notice.articleId} pendingId={notice.pendingId} size={72} /> : null}
					<div style={{ minWidth: 0 }}>
						<div style={{ fontWeight: 700, fontSize: "16px", color: notice.tone === "warn" ? "var(--wa-oro)" : undefined }}>{notice.title}</div>
						{noticeLine ? <div style={{ fontSize: "14px", marginTop: "2px" }}>{pieces(noticeLine.counted)}</div> : notice.detail ? <div style={{ fontSize: "14px", marginTop: "2px" }}>{notice.detail}</div> : null}
						{notice.serialized ? <div style={{ fontSize: "13px", marginTop: "2px", color: "var(--wa-oro)" }}>Lleva número de serie: quien abrió el conteo lo anota en la tabla.</div> : null}
					</div>
				</div>
				{noticeItem && adding === null ? <div style={{ display: "flex", gap: "8px", marginTop: "10px", flexWrap: "wrap" }}>
					{noticeLine ? <>
						<button type="button" className="btn btn-outline-secondary rounded-pill" style={{ minWidth: "56px" }} disabled={busy || noticeLine.counted < 1}
							onClick={() => addProduct({ id: noticeLine.articleId, name: noticeLine.name, waCode: noticeLine.waCode, serialized: noticeLine.serialized } as ScanProduct, -1, notice.code || null)}>−1</button>
						<button type="button" className="btn btn-outline-secondary rounded-pill" style={{ minWidth: "56px" }} disabled={busy}
							onClick={() => addProduct({ id: noticeLine.articleId, name: noticeLine.name, waCode: noticeLine.waCode, serialized: noticeLine.serialized } as ScanProduct, 1, notice.code || null)}>+1</button>
						<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={() => setAdding("")}>Sumar Varias</button>
					</> : null}
					{notice.code ? <button type="button" className="btn btn-link waOroTexto" style={{ padding: "6px 4px" }} disabled={busy} onClick={notThisOne}>No Es Este</button> : null}
				</div> : null}
				{noticeLine && adding !== null ? <form style={{ display: "flex", gap: "8px", marginTop: "10px", alignItems: "center" }}
					onSubmit={(event) => { event.preventDefault(); if (addingQty > 0) addProduct({ id: noticeLine.articleId, name: noticeLine.name, waCode: noticeLine.waCode, serialized: noticeLine.serialized } as ScanProduct, addingQty, notice.code || null); }}>
					<span style={{ fontSize: "14px" }}>Sumar</span>
					<input type="number" inputMode="numeric" min={1} className="form-control" style={{ ...touchField(busy), width: "90px" }} value={adding} autoFocus disabled={busy} onChange={(event) => setAdding(event.target.value)} />
					<button type="submit" className="btn btn-primary rounded-pill" disabled={busy || addingQty < 1}>Sumar</button>
					<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={() => setAdding(null)}>Cancelar</button>
				</form> : null}
			</div> : null}

			{choice ? <div style={{ ...ui.card, padding: "12px" }}>
				<div style={{ fontWeight: 700, marginBottom: "8px" }}>{choice.title}</div>
				<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
					{choice.options.map((option) => {
						const name = option.kind === "product" ? option.product.name : option.pending.name + (option.pending.sizeValue ? " · " + option.pending.sizeValue : "");
						return <button key={option.kind + (option.kind === "product" ? option.product.id : option.pending.id)} type="button" className="btn btn-outline-secondary"
							style={{ display: "flex", gap: "10px", alignItems: "center", textAlign: "left", borderRadius: "10px", padding: "6px" }} disabled={busy} onClick={() => pick(option, choice.code)}>
							<Thumb campusId={campusId} countId={count.id} articleId={option.kind === "product" ? option.product.id : undefined} pendingId={option.kind === "pending" ? option.pending.id : undefined} size={56} />
							<span style={{ minWidth: 0 }}><span style={{ display: "block", fontWeight: 600 }}>{option.kind === "product" && option.product.sizeValue && !choice.allowOther ? option.product.sizeValue : name}</span>
								{option.kind === "pending" ? <span style={{ ...ui.tiny, display: "block" }}>Por dar de alta</span> : null}</span>
						</button>;
					})}
					<div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
						{choice.allowOther ? <button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy}
							onClick={() => { const code = choice.code; setChoice(null); openCreating(code, null, true); }}>Ninguno: Es Otro Producto</button> : null}
						<button type="button" className="btn btn-link" disabled={busy} onClick={() => setChoice(null)}>Cancelar</button>
					</div>
				</div>
			</div> : null}

			<form style={{ display: "flex", gap: "8px" }} onSubmit={(event) => { event.preventDefault(); const code = typed.trim(); setTyped(""); if (!code) return; if (!looksLikeCode(code)) { setSearching(code); return; } handle(code); }}>
				<input type="text" className="form-control" style={touchField(busy)} placeholder="Escribir código" value={typed} disabled={busy} onChange={(event) => setTyped(event.target.value)} />
				<button type="submit" className="btn btn-outline-secondary rounded-pill" style={{ flex: "0 0 auto" }} disabled={busy || typed.trim() === ""}>Buscar</button>
			</form>
			{searching === null ? <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
				<button type="button" className="btn btn-outline-secondary rounded-pill" style={{ ...touchBtn, flex: "1 1 160px" }} disabled={busy} onClick={() => setSearching("")}>Buscar por Nombre</button>
				<button type="button" className="btn btn-outline-secondary rounded-pill" style={{ ...touchBtn, flex: "1 1 160px" }} disabled={busy}
					onClick={() => openCreating(null, null, false)}>Producto Nuevo Sin Código</button>
			</div>
				: <div style={{ ...ui.card, padding: "12px", borderColor: "var(--wa-acento)" }}>
					<div style={{ fontWeight: 700, marginBottom: "6px" }}>Buscar Producto</div>
					<input type="text" className="form-control" style={{ ...touchField(busy), marginBottom: "8px" }} placeholder="Nombre, marca o código WA…" autoFocus value={searching} disabled={busy} onChange={(event) => setSearching(event.target.value)} />
					<div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
						{searching.trim().length < 2 ? <div style={{ ...ui.small, fontSize: "13px" }}>Escribe al menos 2 letras. Tócalo y se suma 1 pieza.</div>
							: found === null ? <div style={{ ...ui.small, fontSize: "13px" }}>Buscando…</div>
							: searchFailed ? <div style={{ fontSize: "13px", color: "var(--wa-oro)" }}>Sin señal: no se pudo buscar. Vuelve a intentar en un momento.</div>
							: found.length < 1 ? <div style={{ ...ui.small, fontSize: "13px" }}>No encontré "{searching.trim()}". Prueba con una sola palabra (por ejemplo, solo "tijera") antes de darlo de alta.</div>
							: found.map((row) => <button key={row.id} type="button" className="btn btn-outline-secondary" disabled={busy}
								style={{ display: "flex", gap: "10px", alignItems: "center", textAlign: "left", borderRadius: "10px", padding: "6px", background: "var(--wa-superficie)" }} onClick={() => pickFound(row)}>
								<Thumb campusId={campusId} countId={count.id} articleId={row.hasPhoto ? row.id : undefined} size={48} />
								<span style={{ minWidth: 0 }}><span style={{ display: "block", fontWeight: 600 }}>{row.name}{row.sizeValue && !row.name.includes(row.sizeValue) ? " · " + row.sizeValue : ""}</span>
									<span style={{ ...ui.tiny, display: "block" }}>{row.variantCount > 0 ? "Contar todas sus tallas y colores" : [row.waCode, row.barcode].filter(Boolean).join(" · ")}</span></span>
							</button>)}
					</div>
					<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" }}>
						{!searchFailed ? <button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy}
							onClick={() => { setSearching(null); setFound(null); openCreating(null, null, false); }}>No Está: Producto Nuevo</button> : null}
						<button type="button" className="btn btn-link" disabled={busy} onClick={() => { setSearching(null); setFound(null); }}>Cancelar</button>
					</div>
				</div>}

			{count.pending.length > 0 ? <div style={{ ...ui.card }}>
				<div style={{ padding: "8px 12px", fontWeight: 700, borderBottom: "1px solid var(--wa-filo)", color: "var(--wa-oro)" }}>Por Dar de Alta ({pendingGroups(count.pending).length})</div>
				{pendingGroups(count.pending).map((group) => <div key={group.first.id} style={{ display: "flex", justifyContent: "space-between", gap: "8px", padding: "8px 12px", borderBottom: "1px solid var(--wa-filo)" }}>
					<div style={{ minWidth: 0 }}><div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group.first.name}{group.label ? " · " + group.label : ""}</div><div style={ui.tiny}>{group.first.barcode || "sin código"} · {group.first.createdBy}</div></div>
					<div style={{ fontWeight: 700, flex: "0 0 auto" }}>{group.pieces}</div>
				</div>)}
			</div> : null}

			<div style={{ ...ui.card }}>
				<div style={{ padding: "8px 12px", fontWeight: 700, borderBottom: "1px solid var(--wa-filo)" }}>Lo Último que Escaneaste</div>
				{recentLines.length < 1 ? <div style={{ ...ui.small, padding: "10px 12px" }}>{totals.products > 0 ? "El conteo ya trae " + totals.products + " productos. Lo que escanees aparece aquí." : "Todavía nada."}</div>
					: recentLines.map((line) => <div key={line.id} style={{ display: "flex", justifyContent: "space-between", gap: "8px", padding: "8px 12px", borderBottom: "1px solid var(--wa-filo)" }}>
						<div style={{ minWidth: 0 }}><div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{line.name}</div><div style={ui.tiny}>{line.barcode || line.waCode}</div></div>
						<div style={{ fontWeight: 700, flex: "0 0 auto" }}>{line.counted}</div>
					</div>)}
				{count.scanners.length > 0 ? <div style={{ ...ui.small, padding: "8px 12px" }}>Escanearon: {count.scanners.map((person) => person.name.split(" ")[0] + " " + person.pieces).join(" · ")}</div> : null}
			</div>
		</div>

		{countingModel !== null ? <ContarPorTallas campusId={campusId} count={count} modelId={countingModel} catalogs={{ sizeTypes, colorSuggestions }} canCreate={count.canCreateProducts}
			onCancel={() => setCountingModel(null)}
			onDone={(data, title) => { merge(data); setCountingModel(null); setNotice({ tone: "ok", title }); confirmPiece(); }} /> : null}
		{creating ? <NuevoProducto key={creating.key} campusId={campusId} countId={count.id} code={creating.code} lookup={creating.lookup} pending={null} sharedCode={creating.shared} canCreate={count.canCreateProducts} sizeTypes={sizeTypes} colorSuggestions={colorSuggestions} currency={currency} light
			onCancel={() => { holdCode(creating.code); setCreating(null); if (fromCountScreen.current) { fromCountScreen.current = false; onClose(); } }}
			onDone={(data, title) => { merge(data); holdCode(creating.code); setCreating(null); setNotice({ tone: "ok", title }); confirmPiece(); if (fromCountScreen.current) { fromCountScreen.current = false; onClose(); } }} /> : null}
	</div>;
};

/** Precio escrito por la persona: "1,250" y "1,250.00" son mil doscientos cincuenta; "12,50" es doce cincuenta; sin "$" ni "L". */
const parsePrice = (text: string): number | null => {
	const clean = text.replace(/^\s*(\$|L|MXN|HNL)\s*/i, "").replace(/\$/g, "");
	const value = parseDecimal(clean);
	return value === null || !Number.isFinite(value) ? null : value;
};

/**
 * Alta de un producto que no existe en WAPP: foto (obligatoria) → IA propone los datos → quien tiene permiso de Productos lo da
 * de alta y lo suma; quien no, lo deja "por dar de alta". Con `pending` la jefa aprueba lo que dejó otra persona.
 */
export const NuevoProducto: React.FC<{
	campusId: number; countId: number; code: string | null; lookup: ScanLookup | null; pending: CountPending | null; sharedCode?: boolean; canCreate: boolean; sizeTypes: SizeTypes;
	colorSuggestions?: string[]; batch?: CountPending[]; currency?: string; light?: boolean; onDone: (count: any, title: string) => void; onCancel: () => void;
}> = ({ campusId, countId, code, lookup, pending, sharedCode, canCreate, sizeTypes, colorSuggestions, batch, currency, light, onDone, onCancel }) => {
	const http = HttpManager.getInstance().stockOpsService;
	const publicHint = lookup?.suggestion || null;
	const barcode = pending ? pending.barcode : code;
	const [photo, setPhoto] = useState<File | null>(null);
	const [photoUrl, setPhotoUrl] = useState<string | null>(null);
	const [reading, setReading] = useState<boolean>(false);
	const [aiNote, setAiNote] = useState<string | null>(null);
	const [name, setName] = useState<string>(pending?.name || publicHint?.name || "");
	const [brand, setBrand] = useState<string>(pending?.brand || publicHint?.brand || "");
	const [detail, setDetail] = useState<string>(pending?.detail || publicHint?.description || "");
	const [unit, setUnit] = useState<string>(pending?.unit || "pieza");
	const [sizeType, setSizeType] = useState<string>(pending?.sizeType || "none");
	const [sizeValue, setSizeValue] = useState<string>(pending?.sizeValue || "");
	// "Cuántas hay" is mandatory and starts empty (an empty or 0 used to silently become 1); a pending brings what was added.
	const [quantity, setQuantity] = useState<string>(pending ? String(pending.quantity) : "");
	const [price, setPrice] = useState<string>(pending?.suggestedPrice ? String(pending.suggestedPrice) : "");
	const priceValue = (): number => parsePrice(price) || 0;
	const [source, setSource] = useState<string>(pending?.source || (publicHint ? publicHint.source : "manual"));
	const [suggestion, setSuggestion] = useState<ScanSuggestion | null>(null);
	const [linking, setLinking] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	/*
		Varias tallas y/o colores de una vez (Esteban, 2026-10-21): se eligen las tallas y colores que trae el producto y se pone
		cuántas hay de cada casilla. Vacía = no se contó; 0 = se contó y no hay. Un "por dar de alta" capturado así (batchKey)
		se aprueba entero, con las cantidades que puso quien contó.
	*/
	const isBatch = !!pending?.batchKey;
	const singlePending = !!pending && !isBatch;
	const batchRows = isBatch ? (batch && batch.length ? batch : [pending as CountPending]) : [];
	// String(): batches saved before the fix could carry waist/shoe sizes as numbers.
	const [draft, setDraft] = useState<VariantDraft>(isBatch && pending?.matrix ? { ...EMPTY_DRAFT, sizes: (pending.matrix.sizes || []).map(String), colors: (pending.matrix.colors || []).map(String) } : EMPTY_DRAFT);
	const [qty, setQty] = useState<Record<string, string>>(() => {
		const out: Record<string, string> = {};
		batchRows.forEach((row) => {
			const header = (row.sizeValue === null && (pending?.matrix?.sizes || []).length > 0) || (!row.colorValue && (pending?.matrix?.colors || []).length > 0);
			if (!header) out[cellKey(row.colorValue || null, row.sizeValue === null || row.sizeValue === undefined ? null : String(row.sizeValue))] = String(row.quantity);
		});
		return out;
	});
	const [withColors, setWithColors] = useState<boolean>(isBatch && (pending?.matrix?.colors || []).length > 0);
	const [extras, setExtras] = useState<File[]>([]);
	const [codeCell, setCodeCell] = useState<string>("");
	const [existingColor, setExistingColor] = useState<string>("");
	const [existingSize, setExistingSize] = useState<string>("");
	const extraRef = useRef<HTMLInputElement>(null);
	const fileRef = useRef<HTMLInputElement>(null);
	const previewRef = useRef<string | null>(null);
	const tookPhoto = useRef<boolean>(false);
	// Fields the person already wrote (or that came from the pending the asesora left): the AI never overwrites them.
	const touched = useRef<Set<string>>(new Set(!pending ? [] : [pending.name ? "name" : "", pending.brand ? "brand" : "", pending.detail ? "detail" : "",
		pending.unit ? "unit" : "", pending.sizeType && pending.sizeType !== "none" ? "size" : ""].filter(Boolean)));
	const markTouched = (key: string) => touched.current.add(key);

	const showPreview = (blob: Blob) => {
		if (previewRef.current) URL.revokeObjectURL(previewRef.current);
		previewRef.current = URL.createObjectURL(blob);
		setPhotoUrl(previewRef.current);
	};
	useEffect(() => () => { if (previewRef.current) URL.revokeObjectURL(previewRef.current); }, []);
	useEffect(() => {
		if (!pending?.hasPhoto) return;
		http.scanPendingPhoto(campusId, countId, pending.id).then((response) => { if (!tookPhoto.current) showPreview(response.data); }).catch(() => undefined);
	}, [pending?.id]);

	const sizeOptions = sizeTypes[sizeType]?.values || [];
	const sizesMissing = Object.keys(sizeTypes).length < 1;
	const matrixMode = !singlePending && (isBatch || sizeType !== "none" || withColors);
	const scaleValues = sizeOptions.map((option) => option.value);
	const gridSizes = sizeType === "none" ? [] : sortSizes(scaleValues, draft.sizes);
	const gridColors = draft.colors;
	const cells = typedCells(gridSizes, gridColors, qty);
	const pickSizeType = (value: string) => {
		markTouched("size");
		setSizeType(value);
		setSizeValue("");
		// Talla única solo tiene "Única": queda elegida. Otra escala: se quedan solo las tallas que existen en ella.
		setDraft((current) => ({ ...current, sizes: sizesFor(value, current.sizes) }));
	};
	const sizesFor = (type: string, sizes: string[]): string[] => type === "onesize" ? ["Única"] : type === "none" ? []
		: sizes.filter((size) => (sizeTypes[type]?.values || []).some((option) => option.value === size));
	// "Bata de Belleza S" → nombre "Bata de Belleza" y la talla S elegida (si es de la escala; "38/39" no lo es).
	const fixName = (found: TallaEnNombre) => {
		markTouched("name");
		setName(found.base);
		if (singlePending) return;
		const type = sizeType !== "none" ? sizeType : found.sizeType;
		if (sizeType === "none") { markTouched("size"); setSizeType(found.sizeType); }
		const scale = (sizeTypes[type]?.values || []).map((option) => option.value);
		if (type === found.sizeType && scale.includes(found.size) && !draft.sizes.includes(found.size)) setDraft({ ...draft, sizes: sortSizes(scale, [...draft.sizes, found.size]) });
	};

	const onPhoto = async (file: File | undefined) => {
		if (!file) return;
		setError(null);
		tookPhoto.current = true;
		const small = await shrinkImage(file);
		setPhoto(small);
		showPreview(small);
		if (lookup && !lookup.aiAvailable) return;
		setReading(true);
		setAiNote(null);
		try {
			const response = await http.scanIdentify(campusId, small, barcode, publicHint ? { name: publicHint.name, brand: publicHint.brand } : null);
			const read = response.data.suggestion;
			if (!read) { setAiNote(response.data.message || "La IA no pudo leer la foto. Llénalo a mano."); return; }
			setSuggestion(read);
			const free = (key: string) => !touched.current.has(key);
			if (read.name && free("name")) setName(read.name);
			if (read.brand && free("brand")) setBrand(read.brand);
			if (read.detail && free("detail")) setDetail(read.detail);
			if (read.unit && free("unit")) setUnit(read.unit);
			if (read.size_type && sizeTypes[read.size_type] && free("size")) {
				setSizeType(read.size_type);
				const valid = (sizeTypes[read.size_type]?.values || []).some((option) => option.value === read.size_value);
				setSizeValue(valid && read.size_value ? read.size_value : "");
				if (!singlePending && !isBatch) setDraft((current) => {
					const kept = sizesFor(read.size_type as string, current.sizes);
					return { ...current, sizes: valid && read.size_value && read.size_type !== "onesize" && !kept.includes(read.size_value) ? [...kept, read.size_value] : kept };
				});
			}
			setSource("ia");
			setAiNote(read.confidence === "baja" ? "La IA no está segura: revisa todo." : "Lo llenó la IA: revísalo.");
		} catch {
			setAiNote("La IA no pudo leer la foto. Llénalo a mano.");
		} finally {
			setReading(false);
		}
	};

	/** Whole number >= 1 typed in "Cuántas hay", or null. */
	const typedQuantity = (): number | null => {
		const text = quantity.trim();
		return /^\d+$/.test(text) && Number(text) >= 1 ? Number(text) : null;
	};
	// Al aprobar un pendiente sin tocar la cantidad, el servidor usa la del momento (un compañero pudo sumar mientras tanto).
	const quantityToSend = (qty: number) => pending && qty === pending.quantity ? undefined : qty;

	const fields = (qty: number, existingModel: boolean) => ({
		name: name.trim(), brand: brand.trim(), detail: detail.trim(), unit, sizeType, sizeValue: sizeType === "none" ? "" : sizeValue,
		barcode: barcode || "", quantity: quantityToSend(qty), source, suggestion: suggestion ? JSON.stringify(suggestion) : undefined,
		pendingId: pending ? pending.id : undefined, sharedCode: sharedCode ? 1 : undefined, existingModel: existingModel ? 1 : undefined,
		// Precio de venta: Super Admin lo deja aprobado; las demás lo sugieren y queda en Precios por aprobar.
		basePrice: Math.round(priceValue() * 100) / 100
	});

	const save = async (existingModel = false) => {
		setError(null);
		if (!photo && !pending?.hasPhoto) { setError("Toma la foto del producto."); return; }
		if (name.trim() === "") { setError("Ponle nombre."); return; }
		const qty = typedQuantity();
		if (qty === null) { setError("Pon cuántas hay."); return; }
		if (!(priceValue() > 0)) { setError("Pon el precio de venta."); return; }
		if (sizeType !== "none" && sizeValue === "") { setError("Elige la talla."); return; }
		setBusy(true);
		try {
			const data = fields(qty, existingModel);
			const response = canCreate ? await http.scanQuickProduct(campusId, countId, data, photo, !!light) : await http.scanPending(campusId, countId, data, photo, !!light);
			onDone(response.data, (canCreate ? "Dado de alta: " : "Por dar de alta: ") + data.name + (data.sizeValue ? " · " + data.sizeValue : ""));
		} catch (requestError: any) {
			setError(scanError(requestError, "No se pudo guardar."));
		} finally {
			setBusy(false);
		}
	};

	const saveMatrix = async (existingModel = false) => {
		setError(null);
		if (!photo && !pending?.hasPhoto) { setError("Toma la foto del producto."); return; }
		if (name.trim() === "") { setError("Ponle nombre."); return; }
		if (!(priceValue() > 0)) { setError("Pon el precio de venta."); return; }
		if (!isBatch && sizeType !== "none" && gridSizes.length < 1) { setError("Elige las tallas que trae."); return; }
		if (!isBatch && sizeType === "none" && gridColors.length < 1) { setError("Elige los colores que trae."); return; }
		if (!isBatch && barcode && cells.length !== 1 && !cells.some((cell) => cellKey(cell.color, cell.size) === codeCell)) {
			setError(cells.length < 1 ? "Pon cuántas hay en la talla o color del código " + barcode + "." : "Elige de cuál es el código " + barcode + ".");
			return;
		}
		const base = {
			name: name.trim(), brand: brand.trim(), detail: detail.trim(), unit, sizeType, source, suggestion: suggestion ? JSON.stringify(suggestion) : undefined,
			basePrice: Math.round(priceValue() * 100) / 100, existingModel: existingModel ? 1 : undefined, existingColor: existingColor || undefined, existingSize: existingSize || undefined
		};
		const picked = cells.find((cell) => cellKey(cell.color, cell.size) === codeCell);
		const data: Record<string, any> = isBatch ? { ...base, pendingId: pending!.id } : {
			...base, barcode: barcode || "", sizes: gridSizes, colors: gridColors, cells: cells.map((cell) => ({ size: cell.size, color: cell.color, quantity: cell.quantity })),
			barcodeCell: barcode && picked ? { size: picked.size, color: picked.color } : undefined
		};
		const pieces = (isBatch ? batchRows.reduce((sum, row) => sum + row.quantity, 0) : cells.reduce((sum, cell) => sum + (Number(cell.quantity) || 0), 0));
		setBusy(true);
		try {
			const response = canCreate || isBatch
				? await http.scanQuickProduct(campusId, countId, data, photo, !!light, extras)
				: await http.scanPending(campusId, countId, data, photo, !!light, extras);
			onDone(response.data, (canCreate || isBatch ? "Dado de alta: " : "Por dar de alta: ") + base.name + " (" + (pieces === 1 ? "1 pieza" : pieces + " piezas") + ")");
		} catch (requestError: any) {
			setError(scanError(requestError, "No se pudo guardar."));
		} finally {
			setBusy(false);
		}
	};

	const onExtraPhoto = async (file: File | undefined) => {
		if (!file || extras.length >= 8) return;
		setExtras([...extras, await shrinkImage(file)]);
	};

	const candidates = (!pending && barcode && lookup?.candidates) || [];
	const [candidateQuery, setCandidateQuery] = useState<string>("");
	const candidateText = useDebounced(candidateQuery, 300);
	// The first list only brings 400 products without a code: a typed name is searched in the server among all of them.
	const [remoteCandidates, setRemoteCandidates] = useState<ScanProduct[] | null>(null);
	useEffect(() => {
		const text = candidateText.trim();
		if (candidates.length < 1 || text.length < 2) { setRemoteCandidates(null); return; }
		let alive = true;
		HttpManager.getInstance().inventoryService.list(campusId, { q: text, kinds: ["article", "vitrina"], includeVariants: 1 }, 1, 30)
			.then((response) => {
				if (!alive) return;
				setRemoteCandidates(((response.data.data || []) as ProductRow[]).filter((row) => row.trackStock && !(row.variantCount > 0) && !row.barcode).slice(0, 6)
					.map((row) => ({ id: row.id, name: row.name, waCode: row.waCode, barcode: row.barcode, sizeValue: row.sizeValue, serialized: !!row.serialized, hasPhoto: row.hasPhoto })));
			})
			.catch(() => { if (alive) setRemoteCandidates(null); });
		return () => { alive = false; };
	}, [candidateText, campusId]);
	const norm = (text: string) => text.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
	// Con lo que escribió la persona (o lo que propuso la IA) se ordenan los productos sin código: los más parecidos primero.
	const words = norm(candidateQuery || name).split(/\s+/).filter((word) => word.length > 2);
	const shownCandidates = remoteCandidates !== null ? remoteCandidates : candidates
		.map((product) => ({ product, score: words.filter((word) => norm(product.name).includes(word)).length }))
		.filter((entry) => candidateQuery.trim() === "" || entry.score > 0)
		.sort((a, b) => b.score - a.score || a.product.name.localeCompare(b.product.name))
		.slice(0, 6).map((entry) => entry.product);
	const taken = !!error && error.startsWith("Ese código ya es de");
	const sameModel = !!error && error.startsWith("Ya hay un modelo");
	// The model already had sizes without color and now colors come: say which color the existing ones are.
	const needsExistingColor = !!error && error.startsWith("Las variantes que ya existen") && error.includes("no tienen color");
	const needsExistingSize = !!error && error.startsWith("Las variantes que ya existen") && error.includes("no tienen talla");

	const sameAsExisting = async () => {
		const qty = typedQuantity();
		if (qty === null) { setError("Pon cuántas hay."); return; }
		setBusy(true);
		setError(null);
		try {
			const found = await http.scanLookup(campusId, barcode || "", countId);
			const products = found.data.products || [];
			if (found.data.status !== "wapp" || products.length !== 1) { setError("Escanéalo otra vez desde la cámara."); return; }
			// Approving a pending: link it to the product that already has the code, so the pending is closed once (summing alone
			// left it open and it could be added again and again).
			const response = pending
				? await http.scanLinkBarcode(campusId, countId, { articleId: products[0].id, quantity: quantityToSend(qty), pendingId: pending.id }, !!light)
				: await http.scanAdd(campusId, countId, products[0].id, qty, null, !!light);
			onDone(response.data, products[0].name + ": sumado");
		} catch (requestError: any) {
			setError(scanError(requestError, "No se pudo sumar."));
		} finally {
			setBusy(false);
		}
	};

	const link = async (product: any) => {
		if (!product.trackStock) { setError("\"" + product.name + "\" tiene tallas: elige la talla."); return; }
		const qty = typedQuantity();
		if (qty === null) { setError("Pon cuántas hay."); return; }
		setBusy(true);
		setError(null);
		try {
			// Ligar el código: jefas siempre; asesoras solo si es un código nuevo (no "No es este", no aprobar pendientes).
			const canLink = (canCreate && (barcode || pending)) || (!canCreate && !!barcode && !sharedCode && !pending && !product.barcode);
			// It is that product but it already has another code: it is counted (and the pending closed) without changing its code.
			const otherCode = !!barcode && !!product.barcode && product.barcode !== barcode;
			const response = canLink && !(otherCode && !pending)
				? await http.scanLinkBarcode(campusId, countId, { articleId: product.id, barcode: barcode || "", quantity: quantityToSend(qty), pendingId: pending ? pending.id : undefined, sharedCode: sharedCode ? 1 : undefined, keepBarcode: otherCode ? 1 : undefined }, !!light)
				: await http.scanAdd(campusId, countId, product.id, qty, null, !!light);
			onDone(response.data, product.name + (otherCode && canLink ? ": contado (se quedó con su código " + product.barcode + ")" : canLink && barcode ? " ya tiene su código" : ""));
		} catch (requestError: any) {
			setError(scanError(requestError, "No se pudo agregar."));
		} finally {
			setBusy(false);
		}
	};

	const parsedPrice = price.trim() === "" ? null : parsePrice(price);
	const quantityField = <div style={{ ...ui.field("50%"), flex: "1 1 0" }}><span style={ui.caption}>Cuántas hay *</span>
		<input type="number" inputMode="numeric" min={1} className="form-control" style={touchField(busy)} value={quantity} disabled={busy} placeholder="0"
			onChange={(event) => { setQuantity(event.target.value.replace(/[^\d]/g, "")); setError(null); }} /></div>;

	return <div style={{ ...sheet, zIndex: 2200 }}>
		<div style={bar}>
			<div style={{ minWidth: 0 }}>
				<div style={{ fontWeight: 700, fontSize: "16px" }}>{pending ? "Dar de Alta" : sharedCode ? "Otro Producto con Este Código" : "Producto Nuevo"}</div>
				<div style={ui.small}>{barcode ? "Código " + barcode : "Sin código"}{pending ? " · lo escaneó " + pending.createdBy : ""}</div>
			</div>
			<button type="button" className="btn btn-outline-secondary rounded-pill" style={{ flex: "0 0 auto" }} disabled={busy} onClick={onCancel}>Cancelar</button>
		</div>
		<div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: "12px", maxWidth: "620px", width: "100%", margin: "0 auto" }}>
			{publicHint ? <div style={{ ...ui.small, fontSize: "13px" }}>{publicHint.source} dice: <b>{publicHint.name}</b>{publicHint.brand ? " · " + publicHint.brand : ""}</div> : null}

			{candidates.length > 0 && !linking ? <div style={{ ...ui.card, padding: "12px", borderColor: "var(--wa-acento)" }}>
				<div style={{ fontWeight: 700, marginBottom: "4px" }}>¿Es alguno de estos?</div>
				<div style={{ ...ui.small, fontSize: "12px", marginBottom: "8px" }}>Productos dados de alta que todavía no tienen código. Si es uno, pon abajo cuántas hay, tócalo y se le guarda este código.</div>
				<input type="text" className="form-control" style={{ ...touchField(busy), marginBottom: "8px" }} placeholder="Buscar por nombre…" value={candidateQuery} disabled={busy} onChange={(event) => setCandidateQuery(event.target.value)} />
				<div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
					{shownCandidates.map((product) => <button key={product.id} type="button" className="btn btn-outline-secondary" disabled={busy}
						style={{ display: "flex", gap: "10px", alignItems: "center", textAlign: "left", borderRadius: "10px", padding: "6px", background: "var(--wa-superficie)" }}
						onClick={() => link({ ...product, trackStock: true })}>
						<Thumb campusId={campusId} countId={countId} articleId={product.id} size={48} />
						<span style={{ minWidth: 0 }}><span style={{ display: "block", fontWeight: 600 }}>{product.name}</span><span style={{ ...ui.tiny, display: "block" }}>{product.waCode}</span></span>
					</button>)}
					{shownCandidates.length < 1 ? <div style={{ ...ui.small, fontSize: "12px" }}>Ninguno con ese nombre.</div> : null}
				</div>
				<div style={{ ...ui.small, fontSize: "12px", marginTop: "8px" }}>Si no es ninguno, llena abajo el producto nuevo.</div>
			</div> : null}

			{linking ? <div style={{ ...ui.card, padding: "12px" }}>
				<div style={{ fontWeight: 700, marginBottom: "6px" }}>¿Cuál producto es?</div>
				<div style={{ display: "flex", gap: "10px", marginBottom: "8px" }}>{quantityField}</div>
				<ProductPicker campusId={campusId} kinds={["article", "vitrina"]} disabled={busy} hideStock placeholder="Busca por nombre…" onPick={link} />
				<div style={{ ...ui.small, marginTop: "6px" }}>{canCreate && barcode ? "Se le guarda este código y se suma al conteo." : "Se suma al conteo."}</div>
				<button type="button" className="btn btn-link btn-sm" style={{ padding: 0, marginTop: "6px" }} disabled={busy} onClick={() => setLinking(false)}>Es un Producto Nuevo</button>
			</div> : <>
				<div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
					<div style={{ width: "96px", height: "96px", flex: "0 0 auto", borderRadius: "10px", border: "1px solid var(--wa-filo)", background: "var(--wa-papel-2)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
						{photoUrl ? <img src={photoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={ui.small}>{pending?.hasPhoto ? "Cargando…" : "Sin foto"}</span>}
					</div>
					<div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
						<input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={(event) => { onPhoto(event.target.files?.[0]); event.target.value = ""; }} />
						<button type="button" className={photoUrl || pending?.hasPhoto ? "btn btn-outline-secondary rounded-pill" : "btn btn-primary rounded-pill"} disabled={busy || reading} onClick={() => fileRef.current?.click()}>{photoUrl || pending?.hasPhoto ? "Otra Foto" : "Tomar Foto"}</button>
						{matrixMode && !isBatch && (photoUrl || pending?.hasPhoto) ? <>
							<input ref={extraRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={(event) => { onExtraPhoto(event.target.files?.[0]); event.target.value = ""; }} />
							<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy || reading || extras.length >= 8} onClick={() => extraRef.current?.click()}>
								{extras.length ? "Más Fotos (" + extras.length + ")" : "Más Fotos"}</button>
						</> : null}
						{isBatch && (pending?.extraPhotos || 0) > 0 ? <span style={ui.small}>{pending!.extraPhotos === 1 ? "1 foto más" : pending!.extraPhotos + " fotos más"}</span> : null}
						{reading ? <span style={{ ...ui.small, fontSize: "13px" }}>Leyendo la foto…</span> : aiNote ? <span style={{ fontSize: "13px", color: "var(--wa-oro)" }}>{aiNote}</span> : null}
					</div>
				</div>

				<div style={ui.field("100%")}><span style={ui.caption}>Nombre</span>
					<input type="text" className="form-control" style={touchField(busy || reading)} maxLength={255} value={name} disabled={busy || reading} onChange={(event) => { markTouched("name"); setName(event.target.value); setError(null); }} /></div>
				<AvisoTalla name={name} busy={busy || reading} onFix={fixName} />
				<div style={{ display: "flex", gap: "10px" }}>
					<div style={{ ...ui.field("50%"), flex: "1 1 0" }}><span style={ui.caption}>Marca</span>
						<input type="text" className="form-control" style={touchField(busy || reading)} maxLength={120} value={brand} disabled={busy || reading} onChange={(event) => { markTouched("brand"); setBrand(event.target.value); }} /></div>
					<div style={{ ...ui.field("50%"), flex: "1 1 0" }}><span style={ui.caption}>Unidad</span>
						<select className="form-select" style={touchField(busy || reading)} value={unit} disabled={busy || reading} onChange={(event) => { markTouched("unit"); setUnit(event.target.value); }}>
							{UNITS.map((entry) => <option key={entry} value={entry}>{entry}</option>)}
						</select></div>
				</div>
				<div style={ui.field("100%")}><span style={ui.caption}>Detalle (color, medida, material)</span>
					<input type="text" className="form-control" style={touchField(busy || reading)} maxLength={255} value={detail} disabled={busy || reading} onChange={(event) => { markTouched("detail"); setDetail(event.target.value); }} /></div>
				<div style={{ display: "flex", gap: "10px" }}>
					<div style={{ ...ui.field("50%"), flex: "1 1 0" }}><span style={ui.caption}>Talla</span>
						<select className="form-select" style={touchField(busy || reading || isBatch)} value={sizeType} disabled={busy || reading || isBatch} onChange={(event) => pickSizeType(event.target.value)}>
							{Object.entries(sizeTypes).length > 0 ? Object.entries(sizeTypes).map(([key, entry]) => <option key={key} value={key}>{entry.label}</option>) : <option value="none">Sin talla</option>}
						</select></div>
					{sizeType !== "none" && !matrixMode ? <div style={{ ...ui.field("50%"), flex: "1 1 0" }}><span style={ui.caption}>Cuál</span>
						<select className="form-select" style={touchField(busy || reading)} value={sizeValue} disabled={busy || reading} onChange={(event) => { markTouched("size"); setSizeValue(event.target.value); }}>
							<option value="">Elige…</option>
							{sizeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
						</select></div> : null}
				</div>
				{sizesMissing ? <div style={{ fontSize: "13px", color: "var(--wa-oro)", marginTop: "-6px" }}>No se cargaron las tallas (sin señal). Si lleva talla, cancela y vuelve a abrirlo.</div> : null}
				{!singlePending && !isBatch && sizeType === "none" && !withColors ? <button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy || reading} onClick={() => setWithColors(true)}>Tiene Varios Colores</button> : null}
				{matrixMode && !isBatch ? <div style={{ ...ui.card, padding: "10px 12px" }}>
					<VariantAxes catalogs={{ sizeTypes, colorSuggestions: colorSuggestions || [] }} sizeType={sizeType}
						facts={{ count: 0, sizes: [], colors: [], colorless: 0, sizeless: 0 }} draft={draft} busy={busy || reading} onChange={setDraft} />
				</div> : null}
				{matrixMode && (gridSizes.length > 0 || gridColors.length > 0) ? <MatrizCantidades sizes={gridSizes} colors={gridColors} qty={qty} busy={busy} readOnly={isBatch} onChange={setQty} /> : null}
				{matrixMode && !isBatch && barcode && cells.length > 1 ? <div style={ui.field("100%")}><span style={ui.caption}>El código {barcode} es de *</span>
					<select className="form-select" style={touchField(busy)} value={codeCell} disabled={busy} onChange={(event) => setCodeCell(event.target.value)}>
						<option value="">Elige…</option>
						{cells.map((cell) => <option key={cellKey(cell.color, cell.size)} value={cellKey(cell.color, cell.size)}>{cellLabel(cell)}</option>)}
					</select></div> : null}
				<div style={{ display: "flex", gap: "10px" }}>
					{matrixMode ? null : quantityField}
					<div style={{ ...ui.field("50%"), flex: "1 1 0" }}><span style={ui.caption}>Precio de venta{currency ? " (" + currency + ")" : ""} *</span>
						<input type="text" inputMode="decimal" className="form-control" style={touchField(busy)} value={price} disabled={busy} placeholder="0.00" onChange={(event) => { setPrice(event.target.value); setError(null); }} />
						{parsedPrice !== null ? <span style={ui.small}>Se guarda: {currency}{parsedPrice.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
							: price.trim() !== "" ? <span style={{ fontSize: "11px", color: "var(--wa-oro)" }}>No es un precio válido.</span> : null}</div>
				</div>

				{error ? <div className="alert alert-danger" style={{ padding: "8px 12px", fontSize: "14px", margin: 0 }}>{error}
					{taken && !sharedCode ? <div style={{ marginTop: "6px" }}><button type="button" className="btn btn-sm btn-primary rounded-pill" disabled={busy} onClick={sameAsExisting}>Es el Mismo: Sumarlo Ahí</button></div> : null}
					{sameModel ? <div style={{ marginTop: "6px" }}><button type="button" className="btn btn-sm btn-primary rounded-pill" disabled={busy} onClick={() => matrixMode ? saveMatrix(true) : save(true)}>{matrixMode ? "Es Otra Talla o Color de Ese" : "Es Otra Talla de Ese"}</button></div> : null}
					{needsExistingColor && matrixMode ? <div style={{ display: "flex", gap: "6px", marginTop: "6px", flexWrap: "wrap" }}>
						<select className="form-select form-select-sm" style={{ maxWidth: "200px" }} value={existingColor} disabled={busy} onChange={(event) => setExistingColor(event.target.value)}>
							<option value="">Las que ya existen son…</option>
							{gridColors.map((color) => <option key={color} value={color}>{color}</option>)}
						</select>
						<button type="button" className="btn btn-sm btn-primary rounded-pill" disabled={busy || !existingColor} onClick={() => saveMatrix(true)}>Seguir</button>
					</div> : null}
					{needsExistingSize && matrixMode ? <div style={{ display: "flex", gap: "6px", marginTop: "6px", flexWrap: "wrap" }}>
						<select className="form-select form-select-sm" style={{ maxWidth: "200px" }} value={existingSize} disabled={busy} onChange={(event) => setExistingSize(event.target.value)}>
							<option value="">Las que ya existen son talla…</option>
							{gridSizes.map((size) => <option key={size} value={size}>{size}</option>)}
						</select>
						<button type="button" className="btn btn-sm btn-primary rounded-pill" disabled={busy || !existingSize} onClick={() => saveMatrix(true)}>Seguir</button>
					</div> : null}
				</div> : null}
				<button type="button" className="btn btn-primary rounded-pill" style={touchBtn} disabled={busy || reading} onClick={() => matrixMode ? saveMatrix() : save()}>
					{busy ? "Guardando…" : isBatch ? "Dar de Alta Todo" : canCreate ? "Dar de Alta y Contar" : "Dejar Por Dar de Alta"}</button>
				{!canCreate ? <div style={{ ...ui.small, fontSize: "12px", marginTop: "-6px" }}>Tu jefa lo revisa y lo da de alta.</div> : null}
				{isBatch ? null : <button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={() => setLinking(true)}>Ya Existe en WAPP: Buscarlo</button>}
			</>}
			{linking && error ? <div className="alert alert-danger" style={{ padding: "8px 12px", fontSize: "14px", margin: 0 }}>{error}</div> : null}
		</div>
	</div>;
};

export default Escaner;
